import type { App } from "@open-booking/server";
import type { CancelBookingPayload, CancellationEvidenceUploadPayload } from "@open-booking/shared";
import { createApi } from "@reduxjs/toolkit/query/react";
import { type EdenCall, edenBaseQuery } from "@/src/common/lib/eden-base-query";

type GetTenantBookingsResponse = App["~Routes"]["tenant"]["bookings"]["get"]["response"]["200"];
type GetTenantBookingResponse = App["~Routes"]["tenant"]["bookings"][":referenceCode"]["get"]["response"]["200"];
type GetTenantBookingContractResponse = App["~Routes"]["tenant"]["bookings"][":referenceCode"]["contract"]["get"]["response"]["200"];
type CancelTenantBookingResponse = App["~Routes"]["tenant"]["bookings"][":referenceCode"]["cancel"]["patch"]["response"]["200"];
type CancellationEvidenceResponse =
	App["~Routes"]["tenant"]["bookings"][":referenceCode"]["cancellation-evidence"]["post"]["response"]["200"];

export const tenantBookingApi = createApi({
	reducerPath: "tenantBookingApi",
	baseQuery: edenBaseQuery(),
	tagTypes: ["TenantBooking"],
	endpoints: (builder) => ({
		getTenantBookings: builder.query<GetTenantBookingsResponse, void>({
			query: (): EdenCall<GetTenantBookingsResponse> => (client) => client.tenant.bookings.get(),
			providesTags: ["TenantBooking"],
		}),
		getTenantBooking: builder.query<GetTenantBookingResponse, string>({
			query:
				(referenceCode): EdenCall<GetTenantBookingResponse> =>
				(client) =>
					client.tenant.bookings({ referenceCode }).get(),
			providesTags: ["TenantBooking"],
		}),
		getTenantBookingContract: builder.query<GetTenantBookingContractResponse, string>({
			query:
				(referenceCode): EdenCall<GetTenantBookingContractResponse> =>
				(client) =>
					client.tenant.bookings({ referenceCode }).contract.get(),
			providesTags: ["TenantBooking"],
		}),
		cancelTenantBooking: builder.mutation<CancelTenantBookingResponse, { referenceCode: string; data: CancelBookingPayload }>({
			query:
				({ referenceCode, data }): EdenCall<CancelTenantBookingResponse> =>
				(client) =>
					client.tenant.bookings({ referenceCode }).cancel.patch(data),
			invalidatesTags: ["TenantBooking"],
		}),
		requestCancellationEvidenceUpload: builder.mutation<
			CancellationEvidenceResponse,
			{ referenceCode: string; data: CancellationEvidenceUploadPayload }
		>({
			query:
				({ referenceCode, data }): EdenCall<CancellationEvidenceResponse> =>
				(client) =>
					client.tenant.bookings({ referenceCode })["cancellation-evidence"].post(data),
		}),
	}),
});

export const {
	useGetTenantBookingsQuery,
	useGetTenantBookingQuery,
	useGetTenantBookingContractQuery,
	useCancelTenantBookingMutation,
	useRequestCancellationEvidenceUploadMutation,
} = tenantBookingApi;
